import React, { useId, useRef, ChangeEvent, ReactNode, KeyboardEvent, useEffect } from 'react';
import Text from '../../Text/Text';
import styles from './Checkbox.module.css';
import cx from 'classnames';
import FullyCheckedIcon from '@/storybook/icons/FullyCheckedIcon';
import PartiallyCheckedIcon from '@/storybook/icons/PartiallyCheckedIcon';
import NotCheckedIcon from '@/storybook/icons/NotCheckedIcon';

export type CheckState = boolean | 'indeterminate';

export const checkboxTestIds = {
  container: 'checkbox-default',
  wrapper: 'checkbox-default-wrapper',
  clickableLabel: 'checkbox-default-clickable-label',
  labelText: 'checkbox-default-label-text',
  mandatoryIndicator: 'checkbox-default-mandatory-indicator',
  helperText: 'checkboxHelperText',
  notCheckedIcon: 'notCheckedIcon',
  partiallyCheckedIcon: 'partiallyCheckedIcon',
  fullyCheckedIcon: 'fullyCheckedIcon',
  labelIcon: 'checkbox-label-icon',
};

export const getCheckboxTestId = {
  clickableLabel: (containerId: string = checkboxTestIds.container) => `${containerId}-clickable-label`,
  labelText: (containerId: string = checkboxTestIds.container) => `${containerId}-label-text`,
  mandatoryIndicator: (containerId: string = checkboxTestIds.container) => `${containerId}-mandatory-indicator`,
  wrapper: (containerId: string = checkboxTestIds.container) => `${containerId}-wrapper`,
};

export const CHECKBOX_STATE_ICONS: Record<string, React.ReactElement | null> = {
  true: <FullyCheckedIcon data-testid={checkboxTestIds.fullyCheckedIcon} />,
  false: <NotCheckedIcon data-testid={checkboxTestIds.notCheckedIcon} />,
  indeterminate: <PartiallyCheckedIcon data-testid={checkboxTestIds.partiallyCheckedIcon} />,
};

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'checked' | 'size'
> {
  'checked': CheckState;
  'onChange'?: (event: ChangeEvent<HTMLInputElement>) => void;
  'label'?: ReactNode;
  'appearance'?: 'normal' | 'error';
  'isMandatory'?: boolean;
  'helperText'?: string;
  'data-testid'?: string;
  'disableFocus'?: boolean;
  'icon'?: React.ReactNode;
  'size'?: 'S' | 'M';
}

interface CheckboxLabelIconProps {
  icon?: ReactNode;
}
const CheckboxLabelIcon = ({ icon }: CheckboxLabelIconProps) => {
  if (!icon) return null;
  return (
    <span data-testid={checkboxTestIds.labelIcon} className={styles.labelIcon}>
      {icon}
    </span>
  );
};

interface CheckboxMandatoryIndicatorProps {
  isMandatory?: boolean;
  dataTestId: string;
}
const CheckboxMandatoryIndicator = ({ isMandatory, dataTestId }: CheckboxMandatoryIndicatorProps) => {
  if (!isMandatory) return null;
  return (
    <span className={styles.isMandatory} data-testid={getCheckboxTestId.mandatoryIndicator(dataTestId)}>
      *
    </span>
  );
};

interface CheckboxLabelTextProps {
  label?: ReactNode;
  labelId: string;
  dataTestId: string;
  children?: ReactNode;
}
const CheckboxLabelText = ({ label, labelId, dataTestId, children }: CheckboxLabelTextProps) => {
  if (!label) return null;
  return (
    <Text as="span" id={labelId} className={styles.label} data-testid={getCheckboxTestId.labelText(dataTestId)}>
      {children}
      {label}
    </Text>
  );
};

export default function Checkbox({
  checked,
  'onChange': handleChangeProp,
  label = '',
  appearance = 'normal',
  isMandatory = false,
  helperText,
  'data-testid': dataTestId = checkboxTestIds.container,
  disableFocus = false,
  size = 'S',
  icon,
  disabled,
  className,
  autoFocus,
  ...rest
}: CheckboxProps) {
  const internalId = useId();
  const labelId = `${internalId}-label`;
  const helperTextId = helperText ? `${internalId}-helper-text` : undefined;
  const checkboxRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const checkboxStateString = checked === 'indeterminate' ? 'indeterminate' : String(!!checked);

  const hasLabelContent = Boolean(icon || label);

  useEffect(() => {
    if (autoFocus && containerRef.current) {
      containerRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (handleChangeProp) {
      handleChangeProp(e);
    }
  };
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !disabled && checkboxRef.current) {
      checkboxRef.current.click();
    }
  };

  const triggerCheckboxClick = () => {
    if (!disabled && checkboxRef.current) {
      checkboxRef.current.click();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      triggerCheckboxClick();
    }
  };

  const renderCheckboxInput = (
    <input
      ref={checkboxRef}
      id={internalId}
      type="checkbox"
      className={cx(styles.checkboxInput, styles.visuallyHidden)}
      checked={checked === 'indeterminate' ? false : checked}
      onChange={handleChange}
      aria-checked={checked === 'indeterminate' ? 'mixed' : checked}
      disabled={disabled}
      tabIndex={-1}
      {...rest}
    />
  );

  const renderCheckboxStructure = (
    <div
      ref={containerRef}
      className={cx(styles.checkboxContainer, styles[`size${size}`], className, {
        [styles.disabled]: disabled,
        [styles.error]: appearance === 'error',
        [styles.disableFocus]: disableFocus,
      })}
      data-checkbox-state={checkboxStateString}
      data-checkbox-appearance={appearance}
      data-testid={dataTestId}
      onClick={handleContainerClick}
      onKeyDown={handleKeyDown}
      role="checkbox"
      aria-checked={checked === 'indeterminate' ? 'mixed' : checked}
      tabIndex={disabled || disableFocus ? -1 : 0}
      aria-labelledby={label ? labelId : undefined}
      aria-disabled={disabled}
      aria-required={isMandatory}
      aria-describedby={helperTextId}
    >
      {renderCheckboxInput}
      <label
        htmlFor={internalId}
        className={styles.checkboxButton}
        data-testid={getCheckboxTestId.clickableLabel(dataTestId)}
      >
        <span className={styles.checkboxVisual}>{CHECKBOX_STATE_ICONS[checkboxStateString]}</span>
        {hasLabelContent && (
          <span className={styles.labelContainer}>
            <CheckboxLabelIcon icon={icon} />
            <CheckboxLabelText label={label} labelId={labelId} dataTestId={dataTestId}>
              <CheckboxMandatoryIndicator isMandatory={isMandatory} dataTestId={dataTestId} />
            </CheckboxLabelText>
          </span>
        )}
      </label>
    </div>
  );

  if (helperText) {
    return (
      <div className={styles.wrapper} data-testid={getCheckboxTestId.wrapper(dataTestId)}>
        {renderCheckboxStructure}
        <Text
          id={helperTextId}
          className={cx(styles.helperText, {
            [styles.errorHelperText]: appearance === 'error',
          })}
          data-testid={checkboxTestIds.helperText}
        >
          {helperText}
        </Text>
      </div>
    );
  }

  return renderCheckboxStructure;
}
